import React, { useMemo, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ScriptBreakdown, ScriptStep } from '../services/api';
import { FlowNode } from './FlowNode';
import { generateFlowLayout } from '../utils/flowLayout';
import './ProcessFlowDiagram.css';

interface ProcessFlowDiagramProps {
  breakdown: ScriptBreakdown;
  onStepClick?: (stepId: string) => void;
  highlightedStep?: string | null;
  executionProgress?: { [stepId: string]: 'pending' | 'running' | 'completed' | 'error' };
}

// Define custom node types
const nodeTypes = {
  customStep: FlowNode,
};

export const ProcessFlowDiagram: React.FC<ProcessFlowDiagramProps> = ({
  breakdown,
  onStepClick,
  highlightedStep,
  executionProgress = {}
}) => {
  // Convert breakdown to ReactFlow nodes and edges
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    return convertBreakdownToFlow(breakdown, executionProgress);
  }, [breakdown, executionProgress]);

  // Apply automatic layout
  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    return generateFlowLayout(initialNodes, initialEdges);
  }, [initialNodes, initialEdges]);

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  // Handle node click
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (onStepClick && node.data.stepId) {
      onStepClick(node.data.stepId);
    }
  }, [onStepClick]);

  // Update node highlighting
  React.useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          isHighlighted: node.data.stepId === highlightedStep
        }
      }))
    );
  }, [highlightedStep, setNodes]);

  return (
    <div className="process-flow-diagram">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        attributionPosition="bottom-left"
      >
        <Background color="#f0f0f0" gap={16} />
        <Controls />
        <MiniMap 
          nodeColor={getNodeColor}
          nodeStrokeWidth={3}
          pannable
          zoomable
        />
      </ReactFlow>
    </div>
  );
};

/**
 * Convert ScriptBreakdown to ReactFlow nodes and edges
 */
function convertBreakdownToFlow(
  breakdown: ScriptBreakdown,
  executionProgress: { [stepId: string]: 'pending' | 'running' | 'completed' | 'error' }
): { nodes: Node[], edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Create nodes from steps
  breakdown.steps.forEach((step, index) => {
    const nodeType = getNodeType(step.type);
    const status = executionProgress[step.id] || 'pending';
    
    nodes.push({
      id: step.id,
      type: 'customStep',
      position: { x: 0, y: 0 }, // Will be set by layout algorithm
      data: {
        stepId: step.id,
        stepNumber: index + 1,
        title: step.title,
        description: step.description,
        type: step.type,
        nodeType: nodeType,
        status: status,
        details: step.details,
        codeLines: step.code_lines
      }
    });
  });

  // Create edges from data flow with validation
  if (breakdown.data_flow && breakdown.data_flow.length > 0) {
    const validStepIds = new Set(nodes.map(n => n.id));
    
    breakdown.data_flow.forEach((flow, index) => {
      // Validate that source and target nodes exist
      if (!validStepIds.has(flow.from) || !validStepIds.has(flow.to)) {
        console.warn(`Skipping invalid data flow: ${flow.from} -> ${flow.to} (node not found)`);
        return;
      }
      
      edges.push({
        id: `flow-${index}`,
        source: flow.from,
        target: flow.to,
        label: flow.description,
        type: 'smoothstep',
        animated: executionProgress[flow.from] === 'running',
        style: {
          stroke: getEdgeColor(executionProgress[flow.from]),
          strokeWidth: 2
        }
      });
    });
  }
  
  // Create sequential edges if no explicit data flow or for orphaned nodes
  if (!edges.length && breakdown.steps.length > 1) {
    // Create sequential edges for linear flow
    for (let i = 0; i < breakdown.steps.length - 1; i++) {
      edges.push({
        id: `seq-${i}`,
        source: breakdown.steps[i].id,
        target: breakdown.steps[i + 1].id,
        type: 'smoothstep',
        animated: executionProgress[breakdown.steps[i].id] === 'running',
        style: {
          stroke: getEdgeColor(executionProgress[breakdown.steps[i].id]),
          strokeWidth: 2
        }
      });
    }
  } else if (edges.length > 0) {
    // Check for orphaned nodes (not connected by any edges)
    const connectedNodes = new Set<string>();
    edges.forEach(edge => {
      connectedNodes.add(edge.source);
      connectedNodes.add(edge.target);
    });
    
    const orphanedNodes = nodes.filter(n => !connectedNodes.has(n.id));
    if (orphanedNodes.length > 0) {
      console.warn(`Found ${orphanedNodes.length} orphaned nodes in flow diagram`);
      // Could optionally connect orphaned nodes sequentially
    }
  }

  return { nodes, edges };
}

/**
 * Determine node type based on step type (using normalized types from backend)
 */
function getNodeType(stepType: string): 'input' | 'processing' | 'output' | 'validation' {
  const type = stepType.toLowerCase();
  
  // Use exact matches for normalized types
  switch(type) {
    case 'file_loading':
      return 'input';
    case 'export':
      return 'output';
    case 'data_validation':
    case 'reconciliation':
      return 'validation';
    case 'calculation':
    case 'transformation':
    case 'aggregation':
      return 'processing';
    default:
      // Fallback to keyword matching for any non-normalized types
      if (type.includes('load') || type.includes('import') || type.includes('read')) {
        return 'input';
      }
      if (type.includes('export') || type.includes('save') || type.includes('output')) {
        return 'output';
      }
      if (type.includes('valid') || type.includes('reconcil')) {
        return 'validation';
      }
      return 'processing';
  }
}

/**
 * Get node color for minimap
 */
function getNodeColor(node: Node): string {
  const status = node.data?.status;
  const nodeType = node.data?.nodeType;
  
  // Status-based colors take priority
  if (status === 'completed') return '#10b981';
  if (status === 'running') return '#3b82f6';
  if (status === 'error') return '#ef4444';
  
  // Type-based colors for pending nodes
  switch (nodeType) {
    case 'input': return '#6366f1';
    case 'output': return '#f97316';
    case 'validation': return '#eab308';
    case 'processing': 
    default: return '#8b5cf6';
  }
}

/**
 * Get edge color based on execution status
 */
function getEdgeColor(status?: string): string {
  switch (status) {
    case 'completed': return '#10b981';
    case 'running': return '#3b82f6';
    case 'error': return '#ef4444';
    default: return '#9ca3af';
  }
}

// Wrap with ReactFlowProvider for proper initialization
export const ProcessFlowDiagramWrapper: React.FC<ProcessFlowDiagramProps> = (props) => {
  return (
    <ReactFlowProvider>
      <ProcessFlowDiagram {...props} />
    </ReactFlowProvider>
  );
};